import React, { createContext } from 'react';
import {
  Option,
  Some,
  None,
  isNone,
  Result,
  Err,
  Ok,
} from '@sniptt/monads/build';
import { intervalToDuration, isAfter, isValid } from 'date-fns';
import axios from 'axios';
import { pipe } from 'fp-ts/lib/function';
import * as B from 'fp-ts/lib/boolean';
import * as O from 'fp-ts/lib/Option';
import * as TE from 'fp-ts/lib/TaskEither';
import * as R from 'fp-ts/lib/Record';
import * as S from 'fp-ts/lib/string';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import jwtDecode from 'jwt-decode';
import { JWTUser } from 'accounting/types';
import { filterNullValues, getEnvironment } from '@schemeserve/global-utils';
import { parseInt } from 'lodash';
import { formatISO, parseISO } from 'date-fns';
import { headers } from 'accounting/BankAccount/locals/GB';

export function logger(...args) {
  const authLogger = window.localStorage.getItem('ssAuthLogger');
  if (authLogger) {
    console.log(...args);
  }
}

export function trackEvent(
  metricLogger: ApplicationInsights,
  name: string,
  tokens?: Tokens,
): void {
  pipe(
    tokens,
    O.fromNullable,
    O.map((tokens) => jwtDecode(tokens.accessToken) as JWTUser),
    O.match(
      () => {
        metricLogger.trackEvent({ name });
        logger({ name });
      },
      (user) =>
        pipe(
          user,
          (user) => ({
            name,
            properties: {
              accessGroup: user.AccessGroup,
              MasterAccountId: user.MasterAccountId,
              MasterUrl: user.MasterUrl,
              AccountId: user.AccountId,
              email: user.PrimaryEmail,
              userId: user.UserId,
              username: user.Username,
              env: getEnvironment(window.location.href),
            },
          }),
          (payload) => {
            metricLogger.trackEvent(payload);
            logger(payload);
          },
          O.some,
        ),
    ),
  );
}

function getRandomIntInRange(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function addJitter(interval: number): number {
  return interval + getRandomIntInRange(10000, 30000);
}

function getFromLocalStorage(key: string): O.Option<string> {
  return pipe(window.localStorage.getItem(key), O.fromNullable);
}

const StorageKeys = {
  Interval: 'ssAuthInterval',
  ExpPeriod: 'ssAuthExpPeriod',
} as const;

export const EXPIRY_PERIOD = pipe(
  StorageKeys.ExpPeriod,
  getFromLocalStorage,
  O.map((value) => parseInt(value, 10)),
  O.getOrElse(() => 1),
);

export const INTERVAL = pipe(
  StorageKeys.Interval,
  getFromLocalStorage,
  O.map((value) => parseInt(value, 10)),
  O.map(addJitter),
  O.getOrElse(() => addJitter(60000)),
);

logger({ EXPIRY_PERIOD, INTERVAL });

export enum AuthState {
  AUTHED = 'authed',
  UNAUTHED = 'unauthed',
}

export enum Keys {
  AccessToken = 'ssAccess',
  Expiry = 'ssExp',
  RefreshToken = 'ssRefresh',
}

export type Tokens = {
  accessToken: string;
  expiry: string;
  refreshToken?: Option<string>;
};

type ReturnType = {
  state: AuthState;
  jwt: string;
  logout: () => void;
};

type Props = {
  children: React.ReactNode;
  refreshUrl: string;
};

// const authContext = createContext({});

export function getTokensFromWindow(): Option<Tokens> {
  logger('windowUser', window?.User);
  if (!window?.User?.AuthInfo?.accessToken || !window?.User?.AuthInfo?.expiry)
    return None;
  const { accessToken, expiry } = window?.User?.AuthInfo;
  logger('windowTokens', { accessToken, expiry, item: window.User.AuthInfo });

  return Some({ accessToken, expiry } as Tokens);
}

export function getTokensFromLocalStorage(): Option<Tokens> {
  const accessToken = window.localStorage.getItem(Keys.AccessToken);
  const expiry = window.localStorage.getItem(Keys.Expiry);
  const refreshToken = window.localStorage.getItem(Keys.RefreshToken);
  logger('localTokens', { accessToken, expiry, refreshToken });

  if (!accessToken || !expiry) return None;

  return Some({
    accessToken,
    expiry,
    refreshToken: refreshToken ? Some(refreshToken) : None,
  });
}

export function removeTokens(): void {
  logger('removingTokens');
  window.localStorage.removeItem(Keys.AccessToken);
  window.localStorage.removeItem(Keys.Expiry);
  window.localStorage.removeItem(Keys.RefreshToken);
}

export function setTokens(tokens: Option<Tokens>): Result<boolean, string> {
  if (tokens.isNone()) {
    logger('unableToSetTokens', tokens);
    return Err('Unable to set tokens');
  }
  const unwrap = tokens.unwrap();
  logger('settingTokens', unwrap);

  if (unwrap?.accessToken !== undefined) {
    window.localStorage.setItem(Keys.AccessToken, unwrap.accessToken);
  }
  if (unwrap?.expiry !== undefined) {
    window.localStorage.setItem(Keys.Expiry, unwrap.expiry);
  }
  if (unwrap.refreshToken?.isSome()) {
    logger('setting refresh-pre', unwrap.refreshToken);
    logger('setting refresh-after', unwrap.refreshToken.unwrap());
    window.localStorage.setItem(
      Keys.RefreshToken,
      unwrap.refreshToken.unwrap(),
    );
  }
  const event = new CustomEvent('localStorageUpdate', {
    detail: { key: 'ssAccess', item: tokens },
  });
  window.dispatchEvent(event);
  return Ok(true);
}

export function getTokens(): Option<Tokens> {
  const localStorageTokens = getTokensFromLocalStorage();
  const windowTokens = getTokensFromWindow();
  logger('localStorage', localStorageTokens);
  logger('window', windowTokens);
  const tokens: Option<Tokens> = localStorageTokens.or<Tokens>(
    windowTokens.or(None),
  );
  logger('tokens', tokens);

  if (isNone(tokens)) {
    return None;
  }

  return tokens;
}

export function convertToUTC(dateString) {
  const hasOffset = /[+-]\d{2}:\d{2}|Z$/.test(dateString);
  const normalizedDateString = hasOffset ? dateString : `${dateString}Z`;
  const date = parseISO(normalizedDateString);
  return formatISO(date, { representation: 'complete' });
}

export function withinExpiryPeriod(tokens: Tokens): boolean {
  return pipe(
    tokens.expiry,
    (exp) => convertToUTC(exp),
    (exp) => ({
      exp,
      currentDate: convertToUTC(new Date().toISOString()),
    }),
    ({ currentDate, exp }) => isAfter(currentDate, exp),
    B.match(
      () =>
        pipe(
          tokens.expiry,
          (exp) => convertToUTC(exp),
          (d) => {
            logger('expriy date', d);
            return d;
          },
          (exp) =>
            intervalToDuration({
              start: convertToUTC(new Date().toISOString()),
              end: exp,
            }),
          (d) => {
            logger('remainingTime', d);
            return d;
          },
          (remaining) => remaining?.minutes ?? 0,
          (minutes) => minutes <= EXPIRY_PERIOD,
        ),
      () => true,
    ),
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// TODO move into global utils api helpers
export function withRetries<E extends { status: number }, A>(
  task: TE.TaskEither<E, A>,
  retries: number,
  delayMs: number = 100,
): TE.TaskEither<E, A> {
  const retry = (retriesLeft: number): TE.TaskEither<E, A> => {
    console.debug('running with retries');
    return pipe(
      () => task(),
      TE.orElse((err) => {
        if (err.status === 401 || retriesLeft <= 0) {
          return TE.left(err);
        }
        return pipe(
          TE.fromIO(() => console.warn(`Retrying... ${retries - retriesLeft + 1}`)),
          TE.chain(() => TE.fromIO(() => sleep(delayMs))),
          TE.chain(() => retry(retriesLeft - 1))
        );
      })
    );
  }

  return retry(retries);
}

export function refreshTokens(
  refreshUrl: string,
  tokens: Tokens,
): TE.TaskEither<Error, Tokens> {
  logger('refreshing: ', refreshUrl);
  return pipe(
    TE.Do,
    TE.bind('method', () =>
      TE.right(tokens.refreshToken?.isSome() ? 'post' : 'get'),
    ),
    TE.bind('opts', ({ method }) =>
      TE.right(
        pipe(
          method === 'get',
          B.match(
            () => ({
              method,
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `bearer ${tokens.accessToken}`,
              },
              data: {
                refreshToken: tokens.refreshToken?.unwrap(),
              },
            }),
            () => ({ method }),
          ),
        ),
      ),
    ),
    TE.chain(({ opts }) =>
      TE.tryCatch(
        () => axios<string, { data: Tokens }>(refreshUrl, opts),
        (err) => err as Error,
      ),
    ),
    // @ts-expect-error CBA as this function will be removed in due course
    (request) => withRetries(request, 3),
    TE.chain((data) =>
      pipe(
        data,
        R.lookup('data'),
        O.map((data) => {
          logger('new token res', data);
          logger('new token', data);
          logger('new token test', data.accessToken === '');

          return {
            accessToken: data.accessToken,
            refreshToken: data?.refreshToken ? Some(data.refreshToken) : None,
            expiry: data.expiry,
          } as Tokens;
        }),
        TE.fromOption(() => new Error('Returned tokens are empty')),
      ),
    ),
  );
}

export function initAuth(
  metricLogger: ApplicationInsights,
): Result<boolean, string> {
  trackEvent(metricLogger, 'initCheckStarted');
  const tokens = getTokens();

  if (isNone(tokens)) {
    trackEvent(metricLogger, 'noInitalTokens');
    return Err('No inital tokens available');
  }

  setTokens(tokens);
  const unwrapedTokens = tokens.unwrap();
  const decodedToken: JWTUser = jwtDecode(unwrapedTokens.accessToken);
  metricLogger.setAuthenticatedUserContext(
    String(decodedToken.UserId),
    String(decodedToken.AccountId),
    true,
  );
  trackEvent(metricLogger, 'UserLoggedIn', unwrapedTokens);

  return Ok(true);
}

export function runProcess(
  url: string,
  metricLogger: ApplicationInsights,
): TE.TaskEither<Error, Tokens> {
  logger('starting check');
  return pipe(
    getTokens(),
    O.fromPredicate((tokens) => !isNone(tokens)),
    O.map((tokens) => tokens.unwrap()),
    TE.fromOption(() => new Error('No vaild tokens when refreshing')),
    TE.chain((tokens) =>
      pipe(
        tokens,
        withinExpiryPeriod,
        B.match(
          () => TE.right(tokens),
          () =>
            pipe(
              refreshTokens(url, tokens),
              TE.tap((token) =>
                pipe(trackEvent(metricLogger, 'TokenRefresh', token), TE.right),
              ),
            ),
        ),
      ),
    ),
  );
}

function pathDoseNotRequierAuth(path: string): (pathToTest: string) => boolean {
  return (pathToTest) => path.includes(pathToTest);
}

function currentPathNotOnPage(currentPath: RegExp): (pathToTest: string) => boolean {
  return (pathToTest) => currentPath.test(pathToTest);
}

export function isANonAuthPage(): boolean {
  const paths = [
    'loginmasterpin.aspx',
    'login2fa.aspx',
    'login.aspx',
    'resetpassword.aspx',
  ];
  const path = window.location.pathname.replace(/\/schemeserve\//, '');
  return paths.some(pathDoseNotRequierAuth(path.toLowerCase()));
}

export function isAnAuthPage(): boolean {
  const paths = [
    'loginmasterpin.aspx',
    'login2fa.aspx',
    'login.aspx',
    'resetpassword.aspx',
  ];

  const currentPath = pipe(
    window.location.pathname,
    S.replace(/\/schemeserve\//, ''),
    S.toLowerCase,
    (path) => new RegExp(path),
  );

  return paths.some(currentPathNotOnPage(currentPath));
}

export function isInSchemeServe(): boolean {
  return window.location.pathname.toLowerCase().includes('schemeserve');
}

export async function logout(
  metricLogger: ApplicationInsights,
  reason?: string,
): Promise<void> {
  logger('LoggingUserOut');
  metricLogger.trackEvent({
    name: 'LoggingUserOut',
    properties: {
      reason,
      env: getEnvironment(window.location.href),
    },
  });
  metricLogger.clearAuthenticatedUserContext();
  // clear local store
  removeTokens();

  if (!window.location.host.includes('localhost')) {
    // hit logout endpoint
    await fetch(`${window.ENVIRONMENT.SecureRoot}?logout=true`, {
      headers: {
        'x-ss-auth-redirect': reason || 'true',
      },
    });

    // redirect to login page
    const from = encodeURI(
      `${window.location.pathname}${window.location.search}`,
    );

    window.location.href = `${window.ENVIRONMENT.SecureRoot}schemeserve/login.aspx?from=${from}`;
  }
}
